/**
 * @fileoverview `<ContraventionCard>` — carte de contravention portail.
 *
 * Affiche uniquement les champs autorisés côté citoyen : LIQ, type
 * d'infraction, montant, date et échéance. Aucune donnée nominative ne doit
 * apparaître dans cette carte.
 *
 * @see Documents/Paymetrust_Spec_Technique_v1.pdf §8.4
 */

import type { ReactNode } from 'react';
import { CalendarDays, Clock3 } from 'lucide-react';
import { Amount } from './amount.tsx';
import { LiquidationNumber } from './liquidation-number.tsx';
import { cn } from '../lib/utils.ts';

export interface ContraventionCardProps {
  liquidationNumber: string;
  infractionType: string;
  amount: number;
  infractionDate?: string;
  dueDate?: string;
  selected?: boolean;
  action?: ReactNode;
  className?: string;
}

function formatDate(value: string | undefined): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function startOfUtcDay(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function formatDaysUntilDue(value: string | undefined): string | null {
  if (!value) return null;

  const due = new Date(value);
  if (Number.isNaN(due.getTime())) return null;

  const todayStart = startOfUtcDay(new Date());
  const dueStart = startOfUtcDay(due);
  const days = Math.ceil((dueStart - todayStart) / 86_400_000);

  if (days < 0) return 'Échéance dépassée';
  if (days === 0) return "Échéance aujourd'hui";
  if (days === 1) return '1 jour restant';
  return `${days} jours restants`;
}

/**
 * Carte de contravention sélectionnable.
 *
 * @param props - Données non nominatives, état sélectionné et action optionnelle.
 * @returns Article visuel mobile-first.
 */
export function ContraventionCard({
  liquidationNumber,
  infractionType,
  amount,
  infractionDate,
  dueDate,
  selected = false,
  action,
  className,
}: ContraventionCardProps): React.JSX.Element {
  const formattedInfractionDate = formatDate(infractionDate);
  const formattedDueDate = formatDate(dueDate);
  const daysUntilDue = formatDaysUntilDue(dueDate);

  return (
    <article
      className={cn(
        'bg-card rounded-lg border p-4 transition-colors',
        selected ? 'border-primary bg-quipux-orange-50/40' : 'border-border',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <LiquidationNumber value={liquidationNumber} copyable />
          <p className="text-quipux-navy-950 text-sm font-medium">{infractionType}</p>
          <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {formattedInfractionDate && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="size-3.5" />
                {formattedInfractionDate}
              </span>
            )}
            {formattedDueDate && <span>Échéance {formattedDueDate}</span>}
            {daysUntilDue && (
              <span className="text-quipux-navy-800 inline-flex items-center gap-1 font-medium">
                <Clock3 className="size-3.5" />
                Délai : {daysUntilDue}
              </span>
            )}
          </div>
        </div>
        <Amount value={amount} compact className="shrink-0" />
      </div>
      {action && <div className="mt-4">{action}</div>}
    </article>
  );
}
